import { useState, useRef } from 'react'
import { useForm } from '@inertiajs/react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Upload, Image, Loader2, Check } from 'lucide-react'

const LOGO_CONFIG = {
  'logo-light': {
    title: 'Logo (Light)',
    description: 'Full logo for light backgrounds',
    aspectRatio: '4:1',
    dimensions: '400×100',
    bgClass: 'bg-white border',
    previewHeight: 'h-20',
  },
  'logo-dark': {
    title: 'Logo (Dark)',
    description: 'Full logo for dark backgrounds',
    aspectRatio: '4:1',
    dimensions: '400×100',
    bgClass: 'bg-zinc-900',
    previewHeight: 'h-20',
  },
  'icon-light': {
    title: 'Icon (Light)',
    description: 'Symbol only for favicons & small spaces',
    aspectRatio: '1:1',
    dimensions: '64×64',
    bgClass: 'bg-white border',
    previewHeight: 'h-16',
  },
  'icon-dark': {
    title: 'Icon (Dark)',
    description: 'Symbol only for favicons & small spaces',
    aspectRatio: '1:1',
    dimensions: '64×64',
    bgClass: 'bg-zinc-900',
    previewHeight: 'h-16',
  },
}

function LogoCard({ logoType, logoData }) {
  const fileInputRef = useRef(null)
  const [uploadSuccess, setUploadSuccess] = useState(false)
  const config = LOGO_CONFIG[logoType]

  const { data, setData, post, processing, progress, reset } = useForm({
    file: null,
  })

  const handleFileChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      setData('file', file)
    }
  }

  const handleUpload = (e) => {
    e.preventDefault()
    if (!data.file) return

    post(`/dev/style-guide/logos/${logoType}`, {
      forceFormData: true,
      preserveScroll: true,
      onSuccess: () => {
        reset()
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
        setUploadSuccess(true)
        setTimeout(() => setUploadSuccess(false), 2000)
      },
    })
  }

  const svgDataUrl = logoData?.content
    ? `data:image/svg+xml;base64,${logoData.content}`
    : null

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">{config.title}</CardTitle>
        <CardDescription>{config.description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Preview Area */}
        <div
          className={`${config.bgClass} rounded-lg p-4 flex items-center justify-center min-h-[120px]`}
        >
          {svgDataUrl ? (
            <img
              src={svgDataUrl}
              alt={config.title}
              className={`${config.previewHeight} max-w-full object-contain`}
              key={logoData.updated_at}
            />
          ) : (
            <div className="flex flex-col items-center text-muted-foreground">
              <Image className="h-8 w-8 mb-2 opacity-50" />
              <span className="text-xs">No logo uploaded</span>
            </div>
          )}
        </div>

        {/* Dimensions Guide */}
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>Aspect ratio: <span className="font-medium text-foreground">{config.aspectRatio}</span></span>
          <span>Recommended: <span className="font-medium text-foreground">{config.dimensions}</span></span>
        </div>

        {/* File Path */}
        <div className="text-xs font-mono text-muted-foreground bg-muted px-2 py-1.5 rounded overflow-x-auto">
          {logoData?.path}
        </div>

        {/* Upload Form */}
        <form onSubmit={handleUpload} className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor={`file-${logoType}`} className="text-xs">
              Upload New SVG
            </Label>
            <Input
              ref={fileInputRef}
              id={`file-${logoType}`}
              type="file"
              accept=".svg,image/svg+xml"
              onChange={handleFileChange}
              disabled={processing}
              className="text-xs"
            />
          </div>

          {progress && (
            <div className="w-full bg-muted rounded-full h-1.5">
              <div
                className="bg-primary h-1.5 rounded-full transition-all"
                style={{ width: `${progress.percentage}%` }}
              />
            </div>
          )}

          <Button
            type="submit"
            size="sm"
            disabled={!data.file || processing}
            className="w-full"
          >
            {processing ? (
              <>
                <Loader2 className="h-3 w-3 animate-spin" />
                Uploading...
              </>
            ) : uploadSuccess ? (
              <>
                <Check className="h-3 w-3" />
                Uploaded!
              </>
            ) : (
              <>
                <Upload className="h-3 w-3" />
                Upload
              </>
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}

export function LogoManager({ logos }) {
  const logoTypes = ['logo-light', 'logo-dark', 'icon-light', 'icon-dark']

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {logoTypes.map((logoType) => (
        <LogoCard
          key={logoType}
          logoType={logoType}
          logoData={logos?.[logoType]}
        />
      ))}
    </div>
  )
}
